import { useEffect, useState } from 'react';
import {
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
} from 'lucide-react';
import { Select } from '@/components/ui/Input';
import type { UseDataTableReturn } from './useDataTable';
import type { PaginatedResult } from './types';

interface Props {
  state: UseDataTableReturn;
  result?: PaginatedResult<unknown>;
}

export function DataTablePagination({ state, result }: Props) {
  const total = result?.total ?? 0;
  const totalPages = Math.max(1, result?.totalPages ?? 1);
  const { page, limit } = state;

  const rangeStart = total === 0 ? 0 : (page - 1) * limit + 1;
  const rangeEnd = Math.min(page * limit, total);

  const goToPage = (target: number) => {
    const clamped = Math.min(Math.max(1, target), totalPages);
    state.onPaginationChange((prev) => ({ ...prev, pageIndex: clamped - 1 }));
  };

  const [pageInput, setPageInput] = useState(String(page));
  useEffect(() => setPageInput(String(page)), [page]);

  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div className="flex flex-col gap-3 border-t border-slate-200 px-4 py-3 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between">
      <span>
        {total === 0
          ? '0 results'
          : `Showing ${rangeStart}–${rangeEnd} of ${total}`}
      </span>

      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-1.5">
          <span className="text-xs text-slate-400">Rows per page</span>
          <Select
            value={limit}
            onChange={(e) =>
              state.onPaginationChange({
                pageIndex: 0,
                pageSize: Number(e.target.value),
              })
            }
            className="w-20 py-1"
          >
            {state.pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Select>
        </label>

        <div className="flex items-center gap-1">
          <button
            className="rounded-md border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => goToPage(1)}
            disabled={!canPrev}
            title="First page"
          >
            <ChevronsLeft className="h-4 w-4" />
          </button>
          <button
            className="rounded-md border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => goToPage(page - 1)}
            disabled={!canPrev}
            title="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          <span className="flex items-center gap-1.5 px-1">
            Page
            <input
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value.replace(/\D/g, ''))}
              onBlur={() => goToPage(Number(pageInput) || 1)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') goToPage(Number(pageInput) || 1);
              }}
              className="w-12 rounded-md border border-slate-300 px-1.5 py-1 text-center text-sm"
              aria-label="Page number"
            />
            of {totalPages}
          </span>

          <button
            className="rounded-md border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => goToPage(page + 1)}
            disabled={!canNext}
            title="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <button
            className="rounded-md border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => goToPage(totalPages)}
            disabled={!canNext}
            title="Last page"
          >
            <ChevronsRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
